// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { Task, WorkCellTask } from '@evolvemep/api-ts';
import { Button, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { RunningTime } from 'components/Mantine/RunningTime';
import { canUncompleteTask } from 'modules/Shop/Tasks/common';
import { ManageTaskTimeModal } from 'modules/Shop/Tasks/ManageTaskTimeModal/ManageTaskTimeModal';
import { CompleteFinalTaskModal } from 'modules/Shop/Tasks/TaskViewer/CompleteFinalTaskModal';
import { UncompleteTaskModal } from 'modules/Shop/Tasks/TaskViewer/UncompleteTaskModal';
import { useCompleteTask } from 'modules/Shop/Tasks/TaskViewer/useCompleteTask';

type Props = {
  task: Task;
  onTaskUpdated: (t: Task) => void;
  workCellTask?: WorkCellTask;
  disabled?: boolean;
};

export const CompleteTaskControls = ({ task, workCellTask, onTaskUpdated, disabled }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const { attemptComplete, completeTask, loading } = useCompleteTask(task, onTaskUpdated);

  return (
    <>
      <Button
        variant="light"
        onClick={() => {
          void attemptComplete().then((didComplete) => {
            if (!didComplete) {
              setShowModal(true);
            }
          });
        }}
        disabled={disabled}
        loading={loading}
      >
        Complete
      </Button>
      <CompleteFinalTaskModal
        workCellTask={workCellTask}
        opened={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={completeTask}
      />
    </>
  );
};

const UncompleteTaskControls = ({ task, workCellTask, onTaskUpdated, disabled }: Props) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Button variant="light" onClick={() => setShowModal(true)} disabled={disabled}>
        Uncomplete
      </Button>
      <UncompleteTaskModal
        task={task}
        workCellTask={workCellTask}
        opened={showModal}
        onClose={() => setShowModal(false)}
        onTaskUncompleted={onTaskUpdated}
      />
    </>
  );
};

export const TaskControls = ({ task, onTaskUpdated, workCellTask, disabled }: Props) => {
  const [manageTimeOpened, setManageTimeOpened] = useState(false);

  if (canUncompleteTask(task)) {
    return (
      <UncompleteTaskControls
        task={task}
        workCellTask={workCellTask}
        onTaskUpdated={onTaskUpdated}
        disabled={disabled}
      />
    );
  }

  return (
    <>
      <Button
        onClick={() => setManageTimeOpened(true)}
        variant="light"
        leftSection={<EvolveIcon icon={task.taskStatusTypeName === 'Started' ? 'Pause' : 'Play'} />}
        rightSection={
          <Text c="dimmed">
            <RunningTime timeInSeconds={task.time.totalTime} isRunning={task.taskStatusTypeName === 'Started'} />
          </Text>
        }
        disabled={disabled}
      >
        {task.taskStatusTypeName === 'Started'
          ? 'Pause'
          : task.taskStatusTypeName === 'Not Started'
            ? 'Start'
            : 'Resume'}
      </Button>
      <ManageTaskTimeModal
        taskId={task.taskId}
        opened={manageTimeOpened}
        onTaskUpdated={onTaskUpdated}
        workCellTask={workCellTask}
        onClose={() => setManageTimeOpened(false)}
      />
      {task.taskStatusTypeName !== 'Completed' && (
        <CompleteTaskControls
          task={task}
          onTaskUpdated={onTaskUpdated}
          workCellTask={workCellTask}
          disabled={disabled}
        />
      )}
    </>
  );
};
